import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { adminApi } from '../adminApi';
import { formatDate } from '../format';
import { StatusBadge } from '../components/StatusBadge';
import { GuideLink } from '../evals/GuideLink';

// Neo refusals (ADR 0119): the coverage-gap signal — what users asked that Neo's
// input gate refused or answered locally. This list returns user-typed query
// text, so it is the SAME content boundary as the transcript: the operator must
// state an access reason first (the server logs it fail-closed), and the text is
// masked by default. This is where the FULL signal surfaces (including refusals
// on a chat that never created a conversation, invisible in the sessions browser).
// `?id=<refusal id>` narrows it to one refusal, whatever its age: the eval case
// inbox links an input-gate refusal candidate here (TOM-262), and the input
// gate's page links a refusal Jev made (ADR 0300). The reason gate and the
// audit are the same.

interface RefusalRow {
  id: string;
  user_id: string;
  short_id: string;
  conversation_id: string | null;
  gate: string;
  reason: string;
  query_text: string | null;
  created_at: string;
}

interface RefusalList {
  refusals: RefusalRow[];
  nextCursor: string | null;
}

const WINDOWS = [7, 30, 90];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function AdminNeoRefusals() {
  const [search, setSearch] = useSearchParams();
  const rawId = search.get('id');
  const onlyId = rawId !== null && UUID.test(rawId) ? rawId.toLowerCase() : null;
  const [reason, setReason] = useState('');
  const [accessReason, setAccessReason] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [data, setData] = useState<RefusalList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reveal, setReveal] = useState(false);

  useEffect(() => {
    if (accessReason === null) return;
    let ignore = false;
    setData(null);
    setError(null);
    adminApi<RefusalList>(
      `/v1/admin/neo/refusals?days=${days}&reason=${encodeURIComponent(accessReason)}${
        onlyId !== null ? `&id=${onlyId}` : ''
      }`
    )
      .then((res) => {
        if (!ignore) setData(res);
      })
      .catch((err) => {
        if (ignore) return;
        const e = err as { body?: { message?: string } };
        setError(e.body?.message ?? 'Failed to load refusals.');
      });
    return () => {
      ignore = true;
    };
  }, [days, accessReason, onlyId]);

  function open(e: FormEvent) {
    e.preventDefault();
    if (reason.trim().length < 3) return;
    setAccessReason(reason.trim());
  }

  if (accessReason === null) {
    return (
      <section className="admin-section">
        <header className="admin-section-header">
          <h1>Neo refusals</h1>
          <GuideLink section="fix-find" />
        </header>
        <p className="admin-hint">
          What users asked that Neo refused or answered locally — the richest signal for coverage
          gaps and false refusals.
        </p>
        {onlyId !== null ? (
          <p className="admin-hint" data-testid="neo-refusal-one">
            You are opening one refusal, <code>{onlyId.slice(0, 8)}</code>.
          </p>
        ) : null}
        <div className="admin-auth-banner">
          This list shows users&apos; refused messages (private content). Your access is logged with
          your account and your stated reason.
        </div>
        <form className="neo-admin-reason" onSubmit={open}>
          <label htmlFor="neo-refusal-reason">Reason for viewing</label>
          <input
            id="neo-refusal-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. reviewing false refusals this week"
            minLength={3}
            maxLength={200}
            required
          />
          <button type="submit" className="admin-button" disabled={reason.trim().length < 3}>
            View refusals
          </button>
        </form>
      </section>
    );
  }

  return (
    <section className="admin-section">
      <header className="admin-section-header">
        <h1>Neo refusals</h1>
        <span className="admin-count">
          {onlyId !== null ? (
            <button
              type="button"
              className="admin-link-button"
              onClick={() => setSearch(new URLSearchParams())}
            >
              Show every refusal
            </button>
          ) : null}
          {onlyId !== null ? null : (
            <span className="admin-seg">
              {WINDOWS.map((w) => (
                <button
                  key={w}
                  type="button"
                  className="admin-link-button"
                  disabled={w === days}
                  onClick={() => setDays(w)}
                >
                  {w}d
                </button>
              ))}
            </span>
          )}
          <button type="button" className="admin-link-button" onClick={() => setReveal((v) => !v)}>
            {reveal ? 'Mask text' : 'Reveal text'}
          </button>
        </span>
        <GuideLink section="fix-find" />
      </header>
      <div className="admin-auth-banner">Viewing users&apos; refused messages — access logged.</div>
      {onlyId !== null ? (
        <p className="admin-hint" data-testid="neo-refusal-one">
          One refusal, <code>{onlyId}</code>, whatever its age.
        </p>
      ) : null}

      {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}
      {data === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {data !== null ? (
        data.refusals.length === 0 ? (
          <p className="admin-empty">
            {onlyId !== null ? 'No refusal with this id.' : 'No refusals in this window.'}
          </p>
        ) : (
          <div className="scroll-x">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>User</th>
                  <th>Gate</th>
                  <th>Reason</th>
                  <th>What the user asked</th>
                  <th>Session</th>
                </tr>
              </thead>
              <tbody>
                {data.refusals.map((r) => (
                  <tr key={r.id} data-testid={`neo-refusal-${r.id}`}>
                    <td>{formatDate(r.created_at)}</td>
                    <td>
                      <Link to={`/admin/users/${r.user_id}`}>
                        <code>{r.short_id}</code>
                      </Link>
                    </td>
                    <td>{r.gate}</td>
                    <td>
                      <StatusBadge status="rejected" label={r.reason} />
                    </td>
                    <td className={reveal ? '' : 'neo-admin-masked'}>{r.query_text ?? '—'}</td>
                    <td>
                      {r.conversation_id !== null ? (
                        <Link to={`/admin/neo/sessions/${r.conversation_id}`}>Open →</Link>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </section>
  );
}
